<template>
  <div class="seating-style-selector">
    <label v-if="label" class="form-label">{{ label }}</label>
    <div class="seating-options" :class="{ 'grid-layout': gridLayout }">
      <div
        v-for="style in seatingStyles"
        :key="style.value"
        class="seating-option"
        :class="{ 'selected': modelValue === style.value, 'disabled': disabled }"
        @click="selectStyle(style.value)"
      >
        <div class="seating-icon-wrapper">
          <img
            :src="getIconPath(style.icon)"
            :alt="style.label"
            class="seating-icon"
            :class="{ 'selected': modelValue === style.value }"
          />
          <div
            v-if="showRadioButton"
            class="radio-wrapper"
          >
            <input
              :id="`seating-${style.value}-${componentId}`"
              type="radio"
              :name="`seating-style-${componentId}`"
              :value="style.value"
              :checked="modelValue === style.value"
              :disabled="disabled"
              class="seating-radio"
              @change="selectStyle(style.value)"
            />
          </div>
        </div>
        <label
          :for="`seating-${style.value}-${componentId}`"
          class="seating-label"
          :class="{ 'selected': modelValue === style.value }"
        >
          {{ style.label }}
        </label>
      </div>
    </div>
    <div v-if="error" class="error-message">{{ error }}</div>
  </div>
</template>

<script>
export default {
  name: 'SeatingStyle',
  props: {
    modelValue: {
      type: String,
      default: null,
      description: 'Selected seating style value'
    },
    label: {
      type: String,
      default: 'Seating Style',
      description: 'Label for the seating style selector'
    },
    disabled: {
      type: Boolean,
      default: false,
      description: 'Whether the selector is disabled'
    },
    showRadioButton: {
      type: Boolean,
      default: true,
      description: 'Whether to show radio buttons'
    },
    gridLayout: {
      type: Boolean,
      default: true,
      description: 'Whether to use grid layout (default) or inline layout'
    },
    availableStyles: {
      type: Array,
      default: () => [],
      description: 'Array of available seating styles to display'
    },
    error: {
      type: String,
      default: null,
      description: 'Error message to display'
    }
  },
  data() {
    return {
      componentId: null,
      defaultSeatingStyles: [
        {
          value: 'cocktail',
          label: 'Cocktail',
          icon: 'seating-cocktail.svg'
        },
        {
          value: 'banquet',
          label: 'Banquet',
          icon: 'seating-banquet.svg'
        },
        {
          value: 'dinner-and-dance',
          label: 'Dinner-and-Dance',
          icon: 'seating-dinner-dance.svg'
        },
        {
          value: 'theatre',
          label: 'Theatre',
          icon: 'seating-theatre.svg'
        },
        {
          value: 'cabaret',
          label: 'Cabaret',
          icon: 'seating-cabaret.svg'
        },
        {
          value: 'u_shaped',
          label: 'U-Shaped',
          icon: 'seating-ushaped.svg'
        },
        {
          value: 'boardroom',
          label: 'Boardroom',
          icon: 'seating-boardroom.svg'
        },
        {
          value: 'classroom',
          label: 'Classroom',
          icon: 'seating-classroom.svg'
        }
      ]
    };
  },
  computed: {
    seatingStyles() {
      // Use provided styles or default to all styles
      if (this.availableStyles.length > 0) {
        return this.availableStyles.map(style => {
          // If it's just a string, find the matching default style
          if (typeof style === 'string') {
            return this.defaultSeatingStyles.find(s => s.value === style) || {
              value: style,
              label: this.capitalize(style),
              icon: `seating-${style}.svg`
            };
          }
          // If it's an object, merge with defaults
          return {
            ...this.defaultSeatingStyles.find(s => s.value === style.value),
            ...style
          };
        });
      }
      return this.defaultSeatingStyles;
    }
  },
  created() {
    this.componentId = Math.random().toString(16).slice(2);
  },
  methods: {
    selectStyle(value) {
      if (!this.disabled) {
        this.$emit('update:modelValue', value);
        this.$emit('change', value);
      }
    },
    getIconPath(iconName) {
      // Handle both relative and absolute paths
      if (iconName.startsWith('/') || iconName.startsWith('http')) {
        return iconName;
      }
      return require(`@/assets/img/seating-style/${iconName}`);
    },
    capitalize(str) {
      return str.charAt(0).toUpperCase() + str.slice(1).replace('-', ' ');
    }
  }
};
</script>

<style lang="scss" scoped>
.seating-style-selector {
  .form-label {
    font-weight: 600;
    margin-bottom: 1rem;
    color: #2c3e50;
  }

  .seating-options {
    display: flex;
    gap: 1rem;
    flex-wrap: wrap;

    &.grid-layout {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
      gap: 1rem;
    }
  }

  .seating-option {
    display: flex;
    flex-direction: column;
    align-items: center;
    cursor: pointer;
    padding: 1rem;
    border: 2px solid #e9ecef;
    border-radius: 8px;
    transition: all 0.3s ease;
    background: white;
    min-width: 120px;

    &:hover:not(.disabled) {
      border-color: #007bff;
      box-shadow: 0 2px 8px rgba(0, 123, 255, 0.15);
      transform: translateY(-2px);
    }

    &.selected {
      border-color: #007bff;
      background-color: #f8f9ff;
      box-shadow: 0 2px 8px rgba(0, 123, 255, 0.25);

      .seating-icon {
        transform: scale(1.05);
      }

      .seating-label {
        color: #007bff;
        font-weight: 600;
      }
    }

    &.disabled {
      opacity: 0.6;
      cursor: not-allowed;
      
      &:hover {
        transform: none;
        box-shadow: none;
        border-color: #e9ecef;
      }
    }
  }

  .seating-icon-wrapper {
    position: relative;
    margin-bottom: 0.5rem;
  }

  .seating-icon {
    width: 60px;
    height: 60px;
    object-fit: contain;
    transition: transform 0.3s ease;
    filter: grayscale(20%);

    &.selected {
      filter: none;
    }
  }

  .radio-wrapper {
    position: absolute;
    top: -5px;
    right: -5px;
  }

  .seating-radio {
    width: 18px;
    height: 18px;
    accent-color: #007bff;
  }

  .seating-label {
    font-size: 0.875rem;
    text-align: center;
    color: #6c757d;
    margin: 0;
    cursor: pointer;
    transition: color 0.3s ease;
    font-weight: 500;

    &.selected {
      color: #007bff;
      font-weight: 600;
    }
  }

  .error-message {
    color: #dc3545;
    font-size: 0.875rem;
    margin-top: 0.5rem;
  }

  // Responsive adjustments
  @media (max-width: 768px) {
    .seating-options {
      &.grid-layout {
        grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
        gap: 0.75rem;
      }
    }

    .seating-option {
      padding: 0.75rem;
      min-width: 100px;
    }

    .seating-icon {
      width: 50px;
      height: 50px;
    }

    .seating-label {
      font-size: 0.8rem;
    }
  }

  // Inline layout variant
  .seating-options:not(.grid-layout) {
    justify-content: flex-start;
    
    .seating-option {
      flex: 0 0 auto;
    }
  }
}
</style>
